import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

// US Eastern time "today" as YYYY-MM-DD. en-CA locale formats as ISO date.
function todayET(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

function cityFromAddress(addr: string | null | undefined): string {
  if (!addr) return "";
  // Typical format: "123 Main St, Bradenton, FL 34202"
  const parts = addr.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 2];
  return "";
}

// ---------- Saved plans ----------

// ---------- Login activity tracking ----------

export const recordLogin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: userRes } = await supabase.auth.getUser();
    const email = userRes.user?.email ?? "";
    if (!email) return { ok: false as const, error: "no email" };

    const { data: existing } = await supabase
      .from("user_login_stats")
      .select("session_count")
      .eq("user_id", userId)
      .maybeSingle();

    const now = new Date().toISOString();
    if (existing) {
      const { error } = await supabase
        .from("user_login_stats")
        .update({
          email,
          session_count: (existing.session_count ?? 0) + 1,
          last_login_at: now,
          updated_at: now,
        })
        .eq("user_id", userId);
      if (error) return { ok: false as const, error: error.message };
    } else {
      const { error } = await supabase
        .from("user_login_stats")
        .insert({
          user_id: userId,
          email,
          session_count: 1,
          first_login_at: now,
          last_login_at: now,
          updated_at: now,
        });
      if (error) return { ok: false as const, error: error.message };
    }
    return { ok: true as const };
  });

export const listMyPlans = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("saved_plans")
      .select("id, name, event_ids, share_token, created_at, updated_at")
      .order("updated_at", { ascending: false });
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, plans: data ?? [] };
  });

const saveSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(120).optional(),
  eventIds: z.array(z.string().uuid()).min(1).max(100),
  wizardData: z.unknown().optional(),
});

export const upsertMyPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => saveSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.id) {
      const { data: row, error } = await supabase
        .from("saved_plans")
        .update({
          name: data.name ?? null,
          event_ids: data.eventIds,
          wizard_data: (data.wizardData as any) ?? null,
        })
        .eq("id", data.id)
        .eq("user_id", userId)
        .select("id, share_token")
        .single();
      if (error || !row) return { ok: false as const, error: error?.message || "Not found" };
      return { ok: true as const, id: row.id, shareToken: row.share_token };
    }
    const { data: row, error } = await supabase
      .from("saved_plans")
      .insert({
        user_id: userId,
        name: data.name ?? null,
        event_ids: data.eventIds,
        wizard_data: (data.wizardData as any) ?? null,
      })
      .select("id, share_token")
      .single();
    if (error || !row) return { ok: false as const, error: error?.message || "Failed" };
    return { ok: true as const, id: row.id, shareToken: row.share_token };
  });

export const deleteMyPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("saved_plans")
      .delete()
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

// ---------- Visits ----------

export const logVisit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({
      eventId: z.string().uuid(),
      visitedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const visited_on = data.visitedOn ?? todayET();
    const { error } = await supabase
      .from("user_event_visits")
      .upsert(
        { user_id: userId, event_id: data.eventId, visited_on },
        { onConflict: "user_id,event_id,visited_on" },
      );
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const, visitedOn: visited_on };
  });

export const removeVisit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ eventId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("user_event_visits")
      .delete()
      .eq("user_id", userId)
      .eq("event_id", data.eventId);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

// ---------- Ratings ----------

export const rateEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({
      eventId: z.string().uuid(),
      rating: z.number().int().min(1).max(5),
      notes: z.string().max(500).optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("user_event_ratings")
      .upsert(
        { user_id: userId, event_id: data.eventId, rating: data.rating, notes: data.notes ?? null },
        { onConflict: "user_id,event_id" },
      );
    if (error) return { ok: false as const, error: error.message };
    // Rating implies the user has been there — auto-log a visit if none exists.
    const { data: existing } = await supabase
      .from("user_event_visits")
      .select("id")
      .eq("user_id", userId)
      .eq("event_id", data.eventId)
      .limit(1);
    if (!existing || existing.length === 0) {
      const visited_on = todayET();
      await supabase
        .from("user_event_visits")
        .upsert(
          { user_id: userId, event_id: data.eventId, visited_on },
          { onConflict: "user_id,event_id,visited_on" },
        );
    }
    return { ok: true as const };
  });

export const clearRating = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ eventId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("user_event_ratings")
      .delete()
      .eq("user_id", userId)
      .eq("event_id", data.eventId);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

export const updateVisitDate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({
      eventId: z.string().uuid(),
      visitedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // Replace any existing visits for this event with a single dated entry.
    const { error: delErr } = await supabase
      .from("user_event_visits")
      .delete()
      .eq("user_id", userId)
      .eq("event_id", data.eventId);
    if (delErr) return { ok: false as const, error: delErr.message };
    const { error } = await supabase
      .from("user_event_visits")
      .insert({ user_id: userId, event_id: data.eventId, visited_on: data.visitedOn });
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

// ---------- Bulk read for personalization ----------

export const getMyHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const [visitsRes, ratingsRes] = await Promise.all([
      supabase
        .from("user_event_visits")
        .select("event_id, visited_on")
        .order("visited_on", { ascending: false }),
      supabase
        .from("user_event_ratings")
        .select("event_id, rating, notes, updated_at"),
    ]);
    if (visitsRes.error) return { ok: false as const, error: visitsRes.error.message };
    if (ratingsRes.error) return { ok: false as const, error: ratingsRes.error.message };
    return {
      ok: true as const,
      visits: visitsRes.data ?? [],
      ratings: ratingsRes.data ?? [],
    };
  });

export const getMyVisitedEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const [visitsRes, ratingsRes] = await Promise.all([
      supabase
        .from("user_event_visits")
        .select("event_id, visited_on")
        .order("visited_on", { ascending: false }),
      supabase
        .from("user_event_ratings")
        .select("event_id, rating"),
    ]);
    if (visitsRes.error) return { ok: false as const, error: visitsRes.error.message };
    if (ratingsRes.error) return { ok: false as const, error: ratingsRes.error.message };

    // Collapse to most-recent visit per event, then merge in rating + event details.
    const lastVisit = new Map<string, string>();
    for (const v of visitsRes.data ?? []) {
      if (!lastVisit.has(v.event_id)) lastVisit.set(v.event_id, v.visited_on);
    }
    const ratingMap = new Map<string, number>();
    for (const r of ratingsRes.data ?? []) ratingMap.set(r.event_id, r.rating);

    // Also include rated events that have no visit row (edge case).
    for (const eid of ratingMap.keys()) {
      if (!lastVisit.has(eid)) lastVisit.set(eid, todayET());
    }

    const ids = Array.from(lastVisit.keys());
    if (ids.length === 0) return { ok: true as const, items: [] };

    const { data: events, error: evErr } = await supabase
      .from("events")
      .select("id, title, venue_name, category, venue_address")
      .in("id", ids);
    if (evErr) return { ok: false as const, error: evErr.message };

    const items = (events ?? []).map((e) => ({
      eventId: e.id,
      title: e.title,
      venueName: e.venue_name,
      category: e.category,
      city: cityFromAddress(e.venue_address),
      visitedOn: lastVisit.get(e.id)!,
      rating: ratingMap.get(e.id) ?? null,
    }));
    items.sort((a, b) => a.title.localeCompare(b.title));
    return { ok: true as const, items };
  });